import { useState } from 'react'
import type { WorkerRole } from '../../../core/index.js'
import type { CensusRequirementsView, MonthHealthView, Posting, ScheduleBoardView, ShiftTypeKey } from '../../types.js'

/**
 * The Today tab's pure parts (facility operations dashboard plan §3, §3a,
 * §3b): the unit and role filter every section reads, the set-up steps of
 * an empty facility, today's coverage per unit and shift, and the open
 * seats in the order they lock.
 */

export interface TodayFilter {
  unitId: string | null
  role: WorkerRole | null
}

export const NO_FILTER: TodayFilter = { unitId: null, role: null }

/** Whether a thing about a unit and a role passes the filter; a thing about no unit or no role passes a filter on it. */
export function matchesTodayFilter(filter: TodayFilter, unitId: string | null, role: WorkerRole | null): boolean {
  return (filter.unitId === null || unitId === null || unitId === filter.unitId) && (filter.role === null || role === null || role === filter.role)
}

const FILTER_KEY = 'autonome.today.filter'

/** The filter, remembered per browser and facility (a convenience: a blocked store just starts unfiltered). */
export function useTodayFilter(facilityId: string): [TodayFilter, (next: TodayFilter) => void] {
  const key = `${FILTER_KEY}.${facilityId}`
  const [filter, setFilter] = useState<TodayFilter>(() => {
    try {
      const stored = window.localStorage.getItem(key)
      return stored ? { ...NO_FILTER, ...(JSON.parse(stored) as Partial<TodayFilter>) } : NO_FILTER
    } catch {
      return NO_FILTER
    }
  })
  const choose = (next: TodayFilter): void => {
    setFilter(next)
    try {
      window.localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // Not remembered; the choice still holds on this page.
    }
  }
  return [filter, choose]
}

// --- The empty facility (§3a): the set-up list, in the order the steps unlock each other.

export type SetupStepKey = 'record' | 'units' | 'plan' | 'workers' | 'publish' | 'census'

export interface SetupStep {
  key: SetupStepKey
  title: string
  body: string
  unlocks: string
  done: boolean
  /** The step that must be done first, where one must. */
  needs: SetupStepKey[]
}

/**
 * The facility's set-up, from what the month's health already finds: no units, no staffing plan, nothing published,
 * no census recorded - and the roster's count. Null when every step is done, which is when the list goes away.
 */
export function setupSteps(health: Pick<MonthHealthView, 'findings'> | null, rosterCount: number): SetupStep[] | null {
  const has = (key: string): boolean => (health?.findings ?? []).some((finding) => finding.key === key)
  const units = !has('no_units')
  const plan = !has('no_staffing_plan')
  const published = !has('nothing_published')
  const census = !has('unit_without_census') && !has('no_units')
  const steps: SetupStep[] = [
    { key: 'record', title: 'The facility record', body: 'Name, time zone, address and phone.', unlocks: 'the day boundaries every score and shift is reckoned in', done: true, needs: [] },
    { key: 'units', title: 'Units and beds', body: 'The units residents live on, with beds and a planned census for each. The census is counted per unit, and so is everything the plan requires.', unlocks: 'recording the census, the requirement and coverage per unit', done: units, needs: [] },
    { key: 'plan', title: 'The staffing plan', body: 'Hours of care per resident day by role (HPPD), split across shifts - or a grid by census band. Load the example and adjust it to the facility assessment.', unlocks: 'what each day requires, publishing shifts from the plan, the census response', done: plan, needs: [] },
    { key: 'workers', title: 'Workers', body: 'Nobody is on the roster. Add them with their role, unit, employment type and credentials, and invite each to the app so they can bid and be reached.', unlocks: 'bidding, the build, offers and flexes', done: rosterCount > 0, needs: [] },
    { key: 'publish', title: 'Publish the month’s shifts', body: 'From the plan at the planned census, or an import. A month whose bidding has closed is assigned from the roster rather than bid for.', unlocks: 'the Month tab, the timeline, the open board', done: published, needs: ['units', 'plan'] },
    { key: 'census', title: 'Record the first census', body: 'One count per unit. From then on the engine checks every recorded day against the plan and the released month each hour.', unlocks: 'the hourly evaluation', done: census, needs: ['units'] },
  ]
  return steps.every((step) => step.done) ? null : steps
}

// --- Today's coverage (§3): required at today's census, held as the board stands.

/** A posting the board is working on: waiting for its offers to open, being offered or blasted. A locked seat is a person's to settle. */
function onTheBoard(phase: Posting['phase']): boolean {
  return phase === 'waiting' || phase === 'offers' || phase === 'first_come'
}

/**
 * A cell's gap, read as the API's `slotCoverage` reads it so the grid and Needs you say the same thing (26 October
 * 2026): the seats run short by decision, those on the board being offered (gold), those open inside the lock and
 * those nothing is working on - both coral, and each a decision under Needs you.
 */
export function cellGap(cell: Pick<CoverageCell, 'required' | 'staffed' | 'open' | 'ranShort'>): { short: number; offered: number; locked: number; ranShort: number } {
  let gap = Math.max(0, cell.required - cell.staffed)
  const take = (count: number): number => {
    const taken = Math.min(gap, count)
    gap -= taken
    return taken
  }
  const ranShort = take(cell.ranShort)
  const offered = take(cell.open.filter((posting) => onTheBoard(posting.phase)).length)
  const locked = take(cell.open.filter((posting) => posting.phase === 'locked').length)
  return { short: gap, offered, locked, ranShort }
}

/** Where a cell stands, for its colour and the counts: a shift that is over is history whatever it held. */
export type CellState = 'none' | 'ended' | 'short' | 'open' | 'covered' | 'over'

export function cellState(cell: CoverageCell): CellState {
  if (cell.required === 0) {
    return 'none'
  }
  if (cell.ended) {
    return 'ended'
  }
  const { short, offered, locked } = cellGap(cell)
  return short + locked > 0 ? 'short' : offered > 0 ? 'open' : cell.staffed > cell.required ? 'over' : 'covered'
}

export interface CoverageCell {
  unitId: string
  shiftType: ShiftTypeKey
  required: number
  staffed: number
  byRole: { role: WorkerRole; required: number; staffed: number }[]
  /** An open posting for the cell that day, with its phase. */
  open: Pick<Posting, 'postingId' | 'phase'>[]
  /** Postings for the cell staff closed as run short. */
  ranShort: number
  /** True once the shift is over: history, never a gap to act on. */
  ended: boolean
}

/** Now where the facility is, and the hours of each shift type, so a cell knows when its shift is over. */
export interface CoverageClock {
  /** The facility's date, `YYYY-MM-DD`. */
  today: string
  /** The facility's time, `HH:mm`. */
  time: string
  spans: Partial<Record<ShiftTypeKey, { start: string; end: string }>>
}

/** Whether a shift of the date is over: before today, or today past its end (a shift that runs past midnight ends tomorrow). */
export function shiftEnded(clock: CoverageClock | null, date: string, shiftType: ShiftTypeKey): boolean {
  if (!clock) {
    return false
  }
  if (date !== clock.today) {
    return date < clock.today
  }
  const span = clock.spans[shiftType]
  return span !== undefined && span.end > span.start && clock.time >= span.end
}

const SHIFT_TYPES: readonly ShiftTypeKey[] = ['day', 'evening', 'night']

/** Per unit and shift type, the seats the day's census requires against the seats the board holds; empty where the facility keeps no plan. */
export function coverageToday(requirements: CensusRequirementsView | null, board: ScheduleBoardView | null, today: string, filter: TodayFilter, clock: CoverageClock | null = null): { unitId: string; name: string; census: number | null; cells: CoverageCell[] }[] {
  if (!requirements) {
    return []
  }
  const held = (board?.schedule ?? []).flatMap((entry) => entry.assignments.filter((assignment) => assignment.date === today).map((assignment) => ({ ...assignment, role: assignment.role ?? null })))
  const postingsToday = (board?.postings ?? []).filter((posting) => posting.shift.date === today && (filter.role === null || posting.shift.role === null || posting.shift.role === filter.role))
  const openToday = postingsToday.filter((posting) => posting.status === 'open' && posting.phase !== 'ended')
  const ranShortToday = postingsToday.filter((posting) => posting.status === 'closed' && posting.ranShort)
  return requirements.units
    .filter((row) => filter.unitId === null || row.unit.id === filter.unitId)
    .map((row) => ({
      unitId: row.unit.id,
      name: row.unit.name,
      census: row.figure ? row.figure.census : null,
      cells: SHIFT_TYPES.map((shiftType) => {
        const shift = row.requirement?.shifts.find((entry) => entry.shiftType === shiftType)
        const roles = (shift?.roles ?? []).filter((entry) => filter.role === null || entry.role === filter.role)
        const here = held.filter((entry) => entry.unitId === row.unit.id && entry.shiftType === shiftType)
        const byRole = roles.map((entry) => ({ role: entry.role, required: entry.seats + (entry.shortSeats ?? 0), staffed: here.filter((assignment) => assignment.role === entry.role).length }))
        const required = byRole.reduce((sum, entry) => sum + entry.required, 0)
        // An assignment that records no role (a board from before 21 October 2026) counts toward the shift, not a role.
        const staffed = filter.role === null ? here.length : byRole.reduce((sum, entry) => sum + entry.staffed, 0)
        const inCell = (posting: { shift: { unitId: string; shiftType: ShiftTypeKey } }) => posting.shift.unitId === row.unit.id && posting.shift.shiftType === shiftType
        return {
          unitId: row.unit.id,
          shiftType,
          required,
          staffed,
          byRole,
          open: openToday.filter(inCell).map(({ postingId, phase }) => ({ postingId, phase })),
          ranShort: ranShortToday.filter(inCell).length,
          ended: shiftEnded(clock, today, shiftType),
        }
      }),
    }))
}

/** The open seats of the board still to come, those the filter keeps, in the order they lock: soonest shift first. A seat whose shift is over is history, not in flight. */
export function openSeatsInFlight(board: ScheduleBoardView | null, filter: TodayFilter): Posting[] {
  return (board?.postings ?? [])
    .filter((posting) => posting.status === 'open' && posting.phase !== 'ended' && matchesTodayFilter(filter, posting.shift.unitId, posting.shift.role))
    .sort((a, b) => `${a.shift.date}T${a.shift.startTime}`.localeCompare(`${b.shift.date}T${b.shift.startTime}`))
}

/** The held seats waiting on a recall. */
export function heldSeats(board: ScheduleBoardView | null, filter: TodayFilter): NonNullable<ScheduleBoardView['held']> {
  return (board?.held ?? []).filter((seat) => (seat.status === 'held' || seat.status === 'recall_offered') && matchesTodayFilter(filter, seat.unitId, seat.role))
}

// --- The week ahead (§3): each day's census and how its coverage stands, coloured as the mockup draws it.

/** `short`: seats no one holds and nobody is being offered; `open`: every gap is on the board being offered; `ok`: covered; `unknown`: no plan to judge by, or the month is not released. */
export type WeekDayState = 'short' | 'open' | 'ok' | 'unknown'

export interface WeekDaySummary {
  date: string
  /** The facility's count over the units shown, recorded or forecast; null when no unit has one. */
  census: number | null
  /** True when every unit shown has an actual count recorded that day (bold); an expected count or a forecast is grey italics. */
  recorded: boolean
  state: WeekDayState
  short: number
  open: number
}


/** One day of the week strip: its census and its seats short or open, from the same reading as Coverage today. */
export function weekDaySummary(requirements: CensusRequirementsView | null, board: ScheduleBoardView | null, date: string, filter: TodayFilter, clock: CoverageClock | null = null): WeekDaySummary {
  const rows = coverageToday(requirements, board, date, filter, clock)
  const counted = rows.filter((row) => row.census !== null)
  const units = (requirements?.units ?? []).filter((row) => filter.unitId === null || row.unit.id === filter.unitId)
  const recorded = units.length > 0 && units.every((row) => row.figure !== null && row.figure.given !== true && row.figure.basis === 'recorded' && row.figure.kind === 'actual')
  const census = counted.length > 0 ? counted.reduce((sum, row) => sum + (row.census ?? 0), 0) : null
  // A shift that is over is history: it no longer colours its day.
  const cells = rows.flatMap((row) => row.cells).filter((cell) => cell.required > 0 && !cell.ended)
  if (cells.length === 0 || board === null) {
    return { date, census, recorded, state: 'unknown', short: 0, open: 0 }
  }
  const tally = cells.reduce(
    (sum, cell) => {
      const { short, offered, locked } = cellGap(cell)
      return { short: sum.short + short + locked, open: sum.open + offered }
    },
    { short: 0, open: 0 },
  )
  const state: WeekDayState = tally.short > 0 ? 'short' : tally.open > 0 ? 'open' : 'ok'
  return { date, census, recorded, state, ...tally }
}
